import { Component, inject, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import {
  Assistant,
  AssistantKey,
  AssistantType,
  listAssistant$,
  Namespace,
  SortDirection,
  TestCase,
  TestCaseKey,
  TestType,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { TestManagementService } from '../test-management.service';
import { NamespaceSelectorComponent } from '../../namespace-selector/namespace-selector.component';
import { AssistantNamePipe, NamespaceUtils } from '../../../core/utils/namespace-utils';
import { isVoiceTest } from '../test-run.utils';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { AiAssistantI18nModule } from '../../../assets/i18n/ai-assistant-i18n.module';

export interface TestCaseSelectorModalData {
  existingTestCaseKeys?: TestCaseKey[];
  testType?: TestType;
}

export interface TestCaseSelectorModalResult {
  selectedTestCaseKeys: TestCaseKey[];
}

interface SelectableTestCase extends TestCase {
  selected: boolean;
}

@Component({
  selector: 'ai-test-case-selector-modal',

  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatListModule,
    MatIconModule,
    GalaxyLoadingSpinnerModule,
    NamespaceSelectorComponent,
    AssistantNamePipe,
    GalaxyFormFieldModule,
    AiAssistantI18nModule,
  ],
  templateUrl: './test-case-selector-modal.component.html',
  styleUrl: './test-case-selector-modal.component.scss',
})
export class TestCaseSelectorModalComponent {
  private readonly dialogRef = inject(MatDialogRef<TestCaseSelectorModalComponent, TestCaseSelectorModalResult>);
  private readonly testManagementService = inject(TestManagementService);
  protected readonly data = inject<TestCaseSelectorModalData>(MAT_DIALOG_DATA, { optional: true });

  // State
  selectedNamespace = signal<Namespace | null>(null);
  assistants = signal<Assistant[]>([]);
  assistantsLoading = signal<boolean>(false);
  selectedAssistant = signal<Assistant | null>(null);
  testCases = signal<SelectableTestCase[]>([]);
  testCasesLoading = signal<boolean>(false);

  // Track existing test case keys to filter them out
  private existingKeys = new Set<string>();

  constructor() {
    // Build set of existing keys for quick lookup
    if (this.data?.existingTestCaseKeys) {
      for (const key of this.data.existingTestCaseKeys) {
        const keyStr = this.buildKeyString(key);
        this.existingKeys.add(keyStr);
      }
    }
  }

  private buildKeyString(key: TestCaseKey): string {
    const nsType = NamespaceUtils.getType(key.assistant?.namespace);
    const nsId = NamespaceUtils.getId(key.assistant?.namespace) || nsType;
    return `${nsType}:${nsId}:${key.assistant?.id}:${key.id}`;
  }

  onNamespaceSelected(namespace: Namespace): void {
    this.selectedNamespace.set(namespace);
    this.selectedAssistant.set(null);
    this.testCases.set([]);
    this.loadAssistants(namespace);
  }

  private loadAssistants(namespace: Namespace): void {
    this.assistantsLoading.set(true);

    const type: AssistantType = isVoiceTest(this.data?.testType)
      ? AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST
      : AssistantType.ASSISTANT_TYPE_UNSPECIFIED;

    listAssistant$({
      filters: { namespace, type, excludedTypes: [] },
      pagingOptions: undefined,
    })
      .subscribe({
        next: (response) => {
          this.assistants.set(response.assistants || []);
          this.assistantsLoading.set(false);
        },
        error: (err) => {
          console.error('Error loading assistants:', err);
          this.assistantsLoading.set(false);
        },
      });
  }

  onAssistantSelected(assistant: Assistant): void {
    this.selectedAssistant.set(assistant);
    this.loadTestCases(assistant);
  }

  private loadTestCases(assistant: Assistant): void {
    this.testCasesLoading.set(true);
    const assistantKey: AssistantKey = {
      id: assistant.id,
      namespace: assistant.namespace,
    };

    this.testManagementService
      .listTestCasesByAssistant(assistantKey, { pageSize: 100 }, '', SortDirection.SORT_DIRECTION_ASCENDING)
      .subscribe({
        next: (response) => {
          let filteredCases = (response.testCases || []).filter((tc) => {
            // Filter out test cases that are already in the suite
            const key: TestCaseKey = {
              assistant: assistantKey,
              id: tc.id,
            };
            return !this.existingKeys.has(this.buildKeyString(key));
          });

          const wantVoice = isVoiceTest(this.data?.testType);
          filteredCases = filteredCases.filter((tc) => isVoiceTest(tc.testType) === wantVoice);

          const cases = filteredCases.map((tc) => ({
            ...tc,
            selected: false,
          }));
          this.testCases.set(cases);
          this.testCasesLoading.set(false);
        },
        error: (err) => {
          console.error('Error loading test cases:', err);
          this.testCasesLoading.set(false);
        },
      });
  }

  toggleTestCase(testCase: SelectableTestCase): void {
    const cases = this.testCases();
    const updated = cases.map((tc) => (tc.id === testCase.id ? { ...tc, selected: !tc.selected } : tc));
    this.testCases.set(updated);
  }

  toggleAllTestCases(): void {
    const cases = this.testCases();
    const allSelected = cases.every((tc) => tc.selected);
    const updated = cases.map((tc) => ({ ...tc, selected: !allSelected }));
    this.testCases.set(updated);
  }

  get titleKey(): string {
    return isVoiceTest(this.data?.testType)
      ? 'AI_ASSISTANT.TESTING.SELECTOR_MODAL.TITLE_VOICE'
      : 'AI_ASSISTANT.TESTING.SELECTOR_MODAL.TITLE_CHAT';
  }

  get noCasesKey(): string {
    return isVoiceTest(this.data?.testType)
      ? 'AI_ASSISTANT.TESTING.SELECTOR_MODAL.NO_VOICE_CASES'
      : 'AI_ASSISTANT.TESTING.SELECTOR_MODAL.NO_CHAT_CASES';
  }

  get selectedCount(): number {
    return this.testCases().filter((tc) => tc.selected).length;
  }

  get allSelected(): boolean {
    const cases = this.testCases();
    return cases.length > 0 && cases.every((tc) => tc.selected);
  }

  close(): void {
    this.dialogRef.close();
  }

  save(): void {
    const assistant = this.selectedAssistant();
    if (!assistant) return;

    const selectedCases = this.testCases().filter((tc) => tc.selected);
    const testCaseKeys: TestCaseKey[] = selectedCases.map((tc) => ({
      assistant: { id: assistant.id, namespace: assistant.namespace },
      id: tc.id,
    }));

    const result: TestCaseSelectorModalResult = {
      selectedTestCaseKeys: testCaseKeys,
    };
    this.dialogRef.close(result);
  }
}
